import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { GrowthCodeSchema } from '@capsico/contracts';
import { errorMessage } from '../api/client';
import { applyGrowthCode, fetchReferral } from '../api/growth';
import { setPendingOfferCode } from './growthEntry';
import { useFormDraft } from './useFormDraft';
import { showToast } from './useToast';

export const referralKey = ['referral'] as const;

interface SharePlugin {
  share(options: {
    title?: string;
    text?: string;
    url?: string;
    dialogTitle?: string;
  }): Promise<void>;
}
/** Native share sheet when @capacitor/share is in the Android build; otherwise unavailable. */
const NativeShare = registerPlugin<SharePlugin>('Share');

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const EMPTY_CODE = '';

/**
 * Refer & earn (W18): your code + share link, friends joined and rewards earned, and a field to
 * apply a friend's / ambassador's / creator's code (draft-saved — a code is not a secret). Sharing
 * uses the Web Share API, then the native share sheet, then copies the link.
 */
export function useReferral(
  enabled: boolean,
  messages: { copied: string; linkCopied: string; shareTitle: string; failed: string },
) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: referralKey,
    queryFn: fetchReferral,
    enabled,
    staleTime: 60_000,
  });
  const [code, setCode] = useState(EMPTY_CODE);
  const [codeError, setCodeError] = useState<string | null>(null);

  const draft = useFormDraft<string>('referral-code:new', code, {
    enabled,
    isEmpty: (value) => value.trim() === '',
    base: EMPTY_CODE,
  });

  const apply = useMutation({
    mutationFn: (value: string) => applyGrowthCode(value),
    onSuccess: (result) => {
      draft.clear();
      setCode(EMPTY_CODE);
      setCodeError(null);
      if (result.offer) setPendingOfferCode(result.offer.code);
      showToast(result.message, { tone: 'positive' });
      void client.invalidateQueries({ queryKey: referralKey });
    },
    onError: (error) => setCodeError(errorMessage(error, messages.failed)),
  });

  const referral = query.data ?? null;

  const share = async () => {
    if (!referral) return;
    const payload = {
      title: messages.shareTitle,
      text: referral.shareText,
      url: referral.shareUrl,
    };
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share(payload);
        return;
      } catch (error) {
        // The customer closed the sheet: nothing else to do.
        if (error instanceof DOMException && error.name === 'AbortError') return;
      }
    }
    if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Share')) {
      try {
        await NativeShare.share({ ...payload, dialogTitle: messages.shareTitle });
        return;
      } catch {
        // Fall through to copying the link.
      }
    }
    if (await copyText(referral.shareText)) showToast(messages.linkCopied, { tone: 'positive' });
  };

  return {
    loading: query.isLoading,
    failed: query.isError && !referral,
    error: query.error ? errorMessage(query.error) : null,
    retry: () => void query.refetch(),
    referral,
    share: () => void share(),
    copyCode: async () => {
      if (referral && (await copyText(referral.code))) {
        showToast(messages.copied, { tone: 'positive' });
      }
    },
    /* apply a friend's code */
    code,
    setCode: (value: string) => {
      setCodeError(null);
      setCode(
        value
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, '')
          .slice(0, 16),
      );
    },
    codeError,
    applying: apply.isPending,
    applyCode: () => {
      const parsed = GrowthCodeSchema.safeParse(code);
      if (!parsed.success) {
        setCodeError(parsed.error.issues[0]?.message ?? messages.failed);
        return;
      }
      apply.mutate(parsed.data);
    },
    draftRestored: draft.restored !== null,
    keepDraft: () => {
      const kept = draft.keep();
      if (kept) setCode(kept);
    },
    discardDraft: draft.discard,
  };
}
